import { FFH_HOSTNAME, SITE_NAME } from '@lib/constants';

// The square brand logo is the only publisher image the site ships; Google
// accepts it for Organization.logo (minimum 112x112).
const ORGANISATION_LOGO_URL = `${FFH_HOSTNAME}/images/FFHub.png`;
export const ORGANISATION_ID = `${FFH_HOSTNAME}/#organization`;

// The footer's social links, without its tracking query strings: sameAs ties
// the brand's profiles to this site for search and answer engines.
export const ORGANISATION_SOCIAL_PROFILES = [
  'https://www.facebook.com/ffootballhub/',
  'https://www.instagram.com/ffh_hq',
  'https://twitter.com/FFH_HQ',
  'https://www.youtube.com/c/FantasyFootballHub/'
];

export type BreadcrumbEntry = {
  label: string;
  /** Site-relative path; the last crumb has none because it is the page itself. */
  path?: string;
};

export const buildBreadcrumbJsonLd = (
  breadcrumbs: BreadcrumbEntry[]
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.label,
    ...(crumb.path ? { item: `${FFH_HOSTNAME}${crumb.path}` } : {})
  }))
});

const HTML_SENSITIVE_CHARACTERS = /[<>&\u2028\u2029]/g;

const toUnicodeEscape = (character: string) =>
  `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`;

/**
 * JSON for a `<script type="application/ld+json">` body. JSON.stringify leaves
 * `<`, `>` and `&` alone, so CMS text containing `</script>` would close the
 * block early, and the two unicode line separators are line terminators to a
 * JS parser but not to JSON. The unicode escapes stay valid JSON for every
 * consumer.
 */
export const serialiseJsonLd = (data: unknown): string =>
  JSON.stringify(data).replace(HTML_SENSITIVE_CHARACTERS, toUnicodeEscape);

/**
 * One publisher identity for every page's structured data. Every `@id` is on
 * the www host, never the CMS host: Yoast's graph pointed at the CMS and
 * overrode our canonical (FFH-2443).
 */
export const buildOrganisationJsonLd = (): Record<string, unknown> => ({
  '@type': 'Organization',
  '@id': ORGANISATION_ID,
  name: SITE_NAME,
  url: FFH_HOSTNAME,
  logo: {
    '@type': 'ImageObject',
    url: ORGANISATION_LOGO_URL
  },
  sameAs: ORGANISATION_SOCIAL_PROFILES
});
